import { useState } from "react";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const DE = [[6.0, 51.9], [6.8, 52.1], [7.0, 52.6], [7.2, 53.3], [8.0, 53.6], [8.6, 53.9], [8.9, 54.4], [8.6, 54.9], [10.0, 54.7], [11.0, 54.4], [12.3, 54.4], [13.5, 54.6], [14.2, 53.9], [14.4, 53.3], [14.1, 52.8], [14.7, 52.1], [14.6, 51.4], [15.0, 51.1], [14.3, 50.9], [12.9, 50.4], [12.1, 50.3], [12.5, 49.8], [13.0, 49.3], [13.8, 48.8], [13.4, 48.4], [12.9, 48.2], [13.0, 47.5], [12.2, 47.6], [10.9, 47.5], [10.2, 47.3], [9.6, 47.5], [8.6, 47.6], [7.6, 47.6], [7.6, 48.1], [8.2, 49.0], [6.4, 49.5], [6.4, 50.3], [6.0, 50.7], [5.9, 51.3], [6.2, 51.6]];
const K = 52;
const px = ([lon, lat]) => [((lon - 5.6) * K * 0.63).toFixed(1), ((55.2 - lat) * K).toFixed(1)];
const OUTLINE = DE.map((p) => px(p).join(",")).join(" ");

export function HeartMap({ c }) {
  const pts = (c.map || []).map((m) => ({ ...m, xy: px([c.coords[m.city][1], c.coords[m.city][0]]) }));
  const cities = [...new Set(pts.map((p) => p.city))];
  return (
    <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-3" data-testid="heart-map">
      <div className="flex items-center justify-between">
        <p className="font-bold flex items-center gap-1.5"><MapPin className="w-4 h-4 text-rose-500" /> Die Reise-Karte</p>
        <span className="text-xs font-bold text-rose-600 bg-rose-50 rounded-full px-2.5 py-1" data-testid="heart-map-count">{cities.length} {cities.length === 1 ? "Stadt" : "Städte"}</span>
      </div>
      <svg viewBox="0 0 310 410" className="w-full max-w-sm mx-auto" role="img" aria-label="Deutschland-Karte der Herz-Kette">
        <defs>
          <radialGradient id="hg" cx="50%" cy="40%" r="70%"><stop offset="0%" stopColor="#fff1f5" /><stop offset="100%" stopColor="#ffe4ec" /></radialGradient>
          <filter id="glow"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>
        <polygon points={OUTLINE} fill="url(#hg)" stroke="#fda4af" strokeWidth="2" strokeLinejoin="round" />
        {pts.length > 1 && <polyline points={pts.map((p) => p.xy.join(",")).join(" ")} fill="none" stroke="#f43f5e" strokeWidth="2" strokeDasharray="5 5" strokeLinecap="round" opacity="0.8" className="heart-route" />}
        {pts.map((p, i) => {
          const last = i === pts.length - 1;
          return (
            <g key={`${p.station}-${i}`} filter="url(#glow)" data-testid={`heart-map-dot-${p.station}`}>
              {last && <circle cx={p.xy[0]} cy={p.xy[1]} r="12" fill="#f43f5e" opacity="0.25" className="animate-ping" style={{ transformOrigin: `${p.xy[0]}px ${p.xy[1]}px` }} />}
              <circle cx={p.xy[0]} cy={p.xy[1]} r={last ? 7 : 4.5} fill={i === 0 ? "#d946ef" : "#f43f5e"} stroke="#fff" strokeWidth="1.5"><title>{`${p.station === 0 ? "Start" : `#${p.station} ${p.name || ""}`} – ${p.city}`}</title></circle>
            </g>
          );
        })}
      </svg>
      {pts.length === 0 ? <p className="text-sm text-slate-400 text-center">Noch keine Stadt eingetragen – die erste Station macht den Anfang!</p> : (
        <p className="text-xs text-slate-500 text-center" data-testid="heart-map-cities">{cities.join(" → ")}</p>
      )}
    </div>
  );
}

export function CityPrompt({ c, onDone }) {
  const [city, setCity] = useState("");
  const save = () => api.post("/heart/city", { city }).then(() => { toast.success(`📍 ${city} ist jetzt auf der Reise-Karte!`); onDone(); }).catch((e) => toast.error(formatErr(e)));
  return (
    <div className="rounded-3xl bg-gradient-to-r from-rose-50 to-fuchsia-50 border border-rose-200 p-4 space-y-2" data-testid="heart-city-prompt">
      <p className="font-bold text-sm flex items-center gap-1.5"><MapPin className="w-4 h-4 text-rose-500" /> Wo hat dich das Herz erreicht?</p>
      <p className="text-xs text-slate-500">Wähle die Stadt in deiner Nähe – sie erscheint auf der Reise-Karte (nur die Stadt, nie deine Adresse).</p>
      <div className="flex gap-2">
        <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="heart-city-select" className="flex-1 min-w-0 h-11 px-3 rounded-2xl border border-pink-200 bg-white text-sm">
          <option value="">Stadt wählen…</option>
          {[...c.cities].sort((a, b) => a.localeCompare(b, "de")).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <button onClick={save} disabled={!city} data-testid="heart-city-save" className="h-11 px-4 rounded-full pink-gradient text-white text-sm font-bold disabled:opacity-40">Eintragen</button>
      </div>
    </div>
  );
}
